import type { AssessedControl, StakeholderRole } from '@wedpo/soc2';
import { STAKEHOLDER_ORDER, roleShortLabels } from '@wedpo/soc2';
import { OwnerBadge } from '@/components/common';

export function P0({ go }: { go: (v: 'p2') => void }) {
  return (
    <section>
      <p className="eyebrow">Phase 0 · unlocked</p>
      <h1 className="title">Foundations</h1>
      <p className="sub">
        Lock in the engagement profile. Everything downstream keys off these decisions.
      </p>
      <div className="grid2">
        <div className="card">
          <h3>Report type</h3>
          <p>
            Type 1 or Type 2. You chose <strong>Type 2</strong>, so the window and evidence
            cadence are active.
          </p>
        </div>
        <div className="card">
          <h3>Observation window</h3>
          <p>
            <strong>01 Jul 2026 - 31 Dec 2026</strong> (6 months). Evidence must be sampled
            across this range.
          </p>
        </div>
        <div className="card">
          <h3>System boundary</h3>
          <p>In-scope systems, hosting, and data flows that define what the report covers.</p>
        </div>
        <div className="card">
          <h3>RACI &amp; team</h3>
          <p>
            7 stakeholder roles mapped. These owners are pre-assigned to every control in
            Phase 2.
          </p>
        </div>
        <div className="card">
          <h3>Initial risk assessment</h3>
          <p>
            CC3 requires a documented risk assessment - captured here as the input to
            scoping.
          </p>
        </div>
        <div className="card">
          <h3>System description</h3>
          <p>Section III of the report drafted from your onboarding answers.</p>
        </div>
      </div>
      <div style={{ marginTop: '1.5rem' }}>
        <button className="btn btn-dark" onClick={() => go('p2')}>
          Preview Phase 2 gap analysis →
        </button>
      </div>
    </section>
  );
}

const evIco = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M3 2.5h7l3 3V13.5H3z"
      stroke="#0D0B1F"
      strokeOpacity=".5"
      strokeWidth="1.3"
    />
    <path d="M9.5 2.5V6h3" stroke="#0D0B1F" strokeOpacity=".5" strokeWidth="1.3" />
  </svg>
);

export function P3({ assessed }: { assessed: AssessedControl[] }) {
  const ev = assessed
    .filter((c) => c.cadence === 'Monthly' || c.cadence === 'Quarterly')
    .slice(0, 9);

  // Action items = anything not yet fully met. Gaps are High priority,
  // partials are Medium. We group them by owning role for the "by role"
  // view and tally High/Medium for the "by gap level" summary.
  const open = assessed.filter((c) => c.status === 'gap' || c.status === 'partial');
  const highs = open.filter((c) => c.status === 'gap').length;
  const meds = open.length - highs;
  const actionGroups = STAKEHOLDER_ORDER.map(
    (role) =>
      [
        role,
        open
          .filter((c) => c.stakeholder === role)
          .sort((a, b) => (a.status === 'gap' ? 0 : 1) - (b.status === 'gap' ? 0 : 1)),
      ] as [StakeholderRole, AssessedControl[]],
  ).filter(([, items]) => items.length > 0);

  return (
    <section>
      <p className="eyebrow">Phase 02 · Plan</p>
      <h1 className="title">Remediation roadmap</h1>
      <p className="sub">
        Every gap becomes a task with an owner and due date. Evidence is collected on a
        recurring cadence across the Type-2 window.
      </p>

      <div className="p3-cta">
        <div className="p3-cta-txt">
          <strong>System Description (Section III)</strong>
          <span>
            Your SOC 2 report opens with a plain-language description of your system: the
            services in scope, the boundary, data flows, and the people who run them.
            Drafting it now keeps your controls and evidence aligned with the narrative
            auditors read first.
          </span>
        </div>
        <button
          className="btn btn-grad p3-cta-btn"
          onClick={() =>
            window.open('/system-description-report.html', '_blank', 'noopener')
          }
        >
          Start System Description Report →
        </button>
      </div>

      <h2 className="sec">Suggested next steps</h2>
      <div className="p3-steps">
        <div className="card">
          <h3>1 · Draft the description</h3>
          <p>Finish Section III while the scope is fresh: auditors read it first.</p>
        </div>
        <div className="card">
          <h3>2 · Own every gap</h3>
          <p>Give each gap a named owner and a due date inside the window.</p>
        </div>
        <div className="card">
          <h3>3 · Automate evidence</h3>
          <p>Stand up the recurring requests so proof is captured on cadence.</p>
        </div>
        <div className="card">
          <h3>4 · Book a check-in</h3>
          <p>Review with WeDPO mid-window while there is time to fix things.</p>
        </div>
      </div>

      <h2 className="sec">
        Evidence requests <span className="recur">recurring across window</span>
      </h2>
      <div className="tblwrap">
        {ev.map((c) => {
          const sCls = c.status === 'na' ? 'gap' : c.status;
          const sLabel =
            c.status === 'met'
              ? 'Collected'
              : c.status === 'partial'
                ? 'In progress'
                : 'Requested';
          return (
            <div key={c.code} className="evrow">
              <div className="ico">{evIco}</div>
              <div style={{ flex: 1 }}>
                <div className="cname">{c.name}</div>
                <div className="code">
                  {c.code} · owner: {roleShortLabels[c.stakeholder]}
                </div>
              </div>
              <span className="recur">{c.cadence}</span>
              <span className={`status s-${sCls}`}>{sLabel}</span>
            </div>
          );
        })}
      </div>
      <div className="note">
        Type 2 note: items marked monthly / quarterly auto-generate recurring collection
        tasks and reminders for the assigned owner across the 6-month window. Type 1 would
        collect these once.
      </div>

      <h2 className="sec">
        Action items{' '}
        <span className="recur">
          {open.length} open · {highs} high · {meds} medium
        </span>
      </h2>
      {actionGroups.length === 0 ? (
        <div className="note">
          No open action items. Every in-scope control is in place or marked not
          applicable.
        </div>
      ) : (
        actionGroups.map(([role, items]) => (
          <div className="p3-owner" key={role}>
            <div className="p3-owner-h">
              <OwnerBadge role={role} />
              <span className="p3-owner-n">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            <div className="tblwrap">
              {items.map((c) => {
                const isGap = c.status === 'gap';
                return (
                  <div key={c.code} className="evrow">
                    <div style={{ flex: 1 }}>
                      <div className="cname">{c.name}</div>
                      <div className="code">
                        {c.code} · {c.cadence}
                      </div>
                    </div>
                    <span className={`status s-${isGap ? 'gap' : 'partial'}`}>
                      {isGap ? 'High' : 'Medium'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

export function P5() {
  return (
    <section>
      <p className="eyebrow">Phase 05 · Auditor</p>
      <h1 className="title">Access &amp; review</h1>
      <p className="sub">
        An internal reviewer walks each control, validates evidence, marks pass/fail, and
        logs exceptions observed during the window before external fieldwork.
      </p>
      <div className="grid3">
        <div className="card">
          <h3>Walkthroughs</h3>
          <p>Test design and operating effectiveness per control.</p>
        </div>
        <div className="card">
          <h3>Exceptions log</h3>
          <p>Document deviations during the observation window and their remediation.</p>
        </div>
        <div className="card">
          <h3>Readiness sign-off</h3>
          <p>A go / no-go before the CPA firm starts.</p>
        </div>
      </div>
    </section>
  );
}

export function P6() {
  return (
    <section>
      <p className="eyebrow">Phase 06 · Finalize</p>
      <h1 className="title">Reports &amp; letters</h1>
      <p className="sub">
        The actual SOC 2 attestation is issued by a licensed CPA firm. This phase manages
        the handoff and tracks the report to issuance.
      </p>
      <div className="grid3">
        <div className="card">
          <h3>Auditor handoff</h3>
          <p>Share the evidence repository and system description through scoped access.</p>
        </div>
        <div className="card">
          <h3>Fieldwork tracking</h3>
          <p>Track requests, PBCs, and findings during fieldwork.</p>
        </div>
        <div className="card">
          <h3>Report &amp; monitor</h3>
          <p>
            Receive the SOC 2 report, then keep continuous monitoring running for the next
            window.
          </p>
        </div>
      </div>
    </section>
  );
}
